/**
 * eReg Context
 *
 * State management for the eReg platform.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as eregApi from '../lib/api';
import type {
  Protocol,
  Folder,
  DocumentType,
  User,
} from '../types';

interface EregContextValue {
  // User state
  user: User | null;
  loading: boolean;
  isAdmin: boolean;

  // Protocols
  protocols: Protocol[];
  protocolsLoading: boolean;
  currentProtocol: Protocol | null;
  setCurrentProtocol: (protocol: Protocol | null) => void;
  refreshProtocols: () => Promise<void>;

  // Folders
  folders: Folder[];
  foldersLoading: boolean;
  currentFolder: Folder | null;
  setCurrentFolder: (folder: Folder | null) => void;
  refreshFolders: (protocolId?: string) => Promise<void>;

  // Document types
  documentTypes: DocumentType[];
  documentTypesLoading: boolean;
  refreshDocumentTypes: () => Promise<void>;

  // Navigation
  siteMode: boolean; // True when viewing site-level documents
  setSiteMode: (mode: boolean) => void;
}

const EregContext = createContext<EregContextValue | undefined>(undefined);

export function EregProvider({ children }: { children: React.ReactNode }) {
  const { user: authUser, currentSite, isAdmin: authIsAdmin, isSuperAdmin } = useAuth();

  // User state
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isAdmin = authIsAdmin || isSuperAdmin;

  // Protocol state
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [protocolsLoading, setProtocolsLoading] = useState(false);
  const [currentProtocol, setCurrentProtocol] = useState<Protocol | null>(null);

  // Folder state
  const [folders, setFolders] = useState<Folder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<Folder | null>(null);

  // Document types
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [documentTypesLoading, setDocumentTypesLoading] = useState(false);

  // Navigation
  const [siteMode, setSiteMode] = useState(false);

  // Track if initial data has been fetched to avoid refetching on token refresh
  const lastAuthUserId = useRef<string | null>(null);
  const protocolsLoaded = useRef(false);

  // Load user data - only when authUser actually changes
  useEffect(() => {
    if (authUser) {
      // Skip if this is the same user we already loaded
      if (lastAuthUserId.current === authUser.id) {
        return;
      }
      lastAuthUserId.current = authUser.id;
      setUser({
        id: authUser.id,
        full_name: authUser.full_name || '',
        email: authUser.email || '',
        role: authUser.role,
      });
      setLoading(false);
    } else {
      lastAuthUserId.current = null;
      protocolsLoaded.current = false; // Reset so protocols reload on next login
      setUser(null);
      setLoading(true);
    }
  }, [authUser]);

  // Load protocols
  const refreshProtocols = useCallback(async () => {
    setProtocolsLoading(true);
    try {
      const data = await eregApi.getProtocols();
      setProtocols(data);
    } catch (error) {
      console.error('[eReg] Error loading protocols:', error);
    } finally {
      setProtocolsLoading(false);
    }
  }, []);

  // Load protocols on mount - only once per user
  useEffect(() => {
    if (user && !protocolsLoaded.current) {
      protocolsLoaded.current = true;
      refreshProtocols();
    }
  }, [user, refreshProtocols]);

  // Load folders for current protocol or site
  const refreshFolders = useCallback(async (protocolId?: string) => {
    setFoldersLoading(true);
    try {
      let data: Folder[];
      if (protocolId) {
        data = await eregApi.getProtocolFolders(protocolId);
      } else if (currentSite && siteMode) {
        data = await eregApi.getSiteFolders(currentSite.id);
      } else if (currentProtocol) {
        data = await eregApi.getProtocolFolders(currentProtocol.id);
      } else {
        data = [];
      }
      setFolders(data);
    } catch (error) {
      console.error('[eReg] Error loading folders:', error);
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }, [currentSite, currentProtocol, siteMode]);

  // Refresh folders when protocol or mode changes
  useEffect(() => {
    if (currentProtocol || siteMode) {
      refreshFolders();
    }
  }, [currentProtocol, siteMode, refreshFolders]);

  // Load document types
  const refreshDocumentTypes = useCallback(async () => {
    setDocumentTypesLoading(true);
    try {
      const data = await eregApi.getDocumentTypes({
        network_id: currentSite?.network_id,
        site_id: currentSite?.id,
      });
      setDocumentTypes(data);
    } catch (error) {
      console.error('[eReg] Error loading document types:', error);
    } finally {
      setDocumentTypesLoading(false);
    }
  }, [currentSite]);

  // Load document types on mount
  useEffect(() => {
    if (user && currentSite) {
      refreshDocumentTypes();
    }
  }, [user, currentSite, refreshDocumentTypes]);

  const value: EregContextValue = {
    user,
    loading,
    isAdmin,
    protocols,
    protocolsLoading,
    currentProtocol,
    setCurrentProtocol,
    refreshProtocols,
    folders,
    foldersLoading,
    currentFolder,
    setCurrentFolder,
    refreshFolders,
    documentTypes,
    documentTypesLoading,
    refreshDocumentTypes,
    siteMode,
    setSiteMode,
  };

  return (
    <EregContext.Provider value={value}>
      {children}
    </EregContext.Provider>
  );
}

export function useEreg() {
  const context = useContext(EregContext);
  if (!context) {
    throw new Error('useEreg must be used within an EregProvider');
  }
  return context;
}

export default EregContext;
